import type { UserApplication } from "../domain/types";
import type { Gender, MatchProfile, MatchScales } from "./types";
import { oppositeGender, scalesFromAnswers } from "./types";

/**
 * Hard rules to enter the live matching pool from a UserApplication:
 * - verified account
 * - identity verification approved
 * - Christian
 * - binary gender
 * - complete scales 1–10
 * - age ≥ 18
 * - loveReady not explicitly false
 */
export function isEligibleForMatching(app: UserApplication): boolean {
  return getIneligibilityReasons(app).length === 0;
}

/** Human-readable reasons when an approved app cannot enter the pool yet. */
export function getIneligibilityReasons(app: UserApplication): string[] {
  const reasons: string[] = [];
  if (app.status !== "verified") {
    reasons.push(`application status is "${app.status}" (need verified)`);
  }
  if (app.verification.status !== "approved") {
    reasons.push(`verification is "${app.verification.status}" (need approved)`);
  }
  if (app.isChristian !== true) {
    reasons.push("must affirm Christian faith");
  }
  if (app.gender !== "male" && app.gender !== "female") {
    reasons.push("gender must be male or female");
  }
  if (app.loveReady === false) {
    reasons.push("loveReady is false");
  }
  const age = Number(app.age);
  if (!Number.isFinite(age) || age < 18) {
    reasons.push("age must be 18+");
  }
  if (!scalesCompleteStrict(app.scales)) {
    reasons.push("loyalty/honesty/passion/commitment must each be 1–10");
  }
  return reasons;
}

function scalesCompleteStrict(scales: UserApplication["scales"]): boolean {
  const keys: (keyof MatchScales)[] = ["loyalty", "honesty", "passion", "commitment"];
  return keys.every((k) => {
    const n = scales[k];
    return typeof n === "number" && n >= 1 && n <= 10;
  });
}

/** Adapter for approved applications → MatchProfile. */
export function matchProfileFromApplication(app: UserApplication): MatchProfile | null {
  if (!isEligibleForMatching(app)) return null;
  const gender = app.gender as Gender;
  const userId = app.userId || app.id;
  return {
    id: userId,
    firstName: app.firstName.trim() || "Member",
    age: Number(app.age),
    city: app.city.trim() || "—",
    country: app.country?.trim().toUpperCase() || undefined,
    preferredPartnerCountry: app.preferredPartnerCountry || "any",
    gender,
    profession: app.profession.trim() || "—",
    aboutYou: app.aboutYou.trim(),
    lookingFor: app.lookingFor.trim(),
    hobbies: app.hobbies.trim(),
    scales: scalesFromAnswers(app.scales),
    source: "pool",
    // UN8: carry the questionnaire into the deck. Undefined stays
    // undefined, which is what keeps pre-questionnaire members matchable.
    answers: app.answers,
  };
}

export function gendersCompatible(viewer: Gender, candidate: Gender): boolean {
  return candidate === oppositeGender(viewer);
}
